/**
 * MapForge 상태. 문서(JSON)는 스토어에, 자산 원형(three 객체)은 모듈 맵에 둔다 —
 * zustand 에 three 객체를 넣으면 비교·직렬화가 무거워진다. assetsVersion 으로 리렌더를 알린다.
 */

import { create } from 'zustand';
import * as THREE from 'three';
import {
  buildFlatTemplate, buildPropTemplate, emptyDoc, eraseTile, fillFloor, loadImage, parseDoc, placeTile, toDataUrl,
  type MapDoc, type Placement, type TileAsset, type TileKind,
} from '../core/map/kit.ts';
import { forgeFromBlob } from '../core/pipeline.ts';
import { rasterToCanvas } from '../core/imaging/raster.ts';
import type { MaterialMaps } from '../core/types.ts';

export const mapAssets = new Map<string, TileAsset>();

export type MapTool = 'paint' | 'erase' | 'spawn';

export interface MapStore {
  doc: MapDoc;
  assetsVersion: number;
  selected: string | null;
  tool: MapTool;
  rot: number;
  level: number;
  scale: number;
  busy: string | null;
  error: string | null;
  showGrid: boolean;
  showHero: boolean;
  addImages: (files: File[], kind: TileKind) => Promise<void>;
  addFromUrl: (url: string, name: string, kind: TileKind) => Promise<void>;
  select: (id: string | null) => void;
  setTool: (t: MapTool) => void;
  setRot: (r: number) => void;
  setLevel: (l: number) => void;
  setScale: (s: number) => void;
  resize: (w: number, d: number) => void;
  paintAt: (x: number, z: number) => void;
  fill: () => void;
  clear: () => void;
  removeAsset: (id: string) => void;
  loadDoc: (text: string) => Promise<void>;
  toggle: (k: 'showGrid' | 'showHero') => void;
}

let seq = 0;
const newId = (kind: TileKind) => `${kind}-${Date.now().toString(36)}-${(seq++).toString(36)}`;

function thumbOf(img: HTMLImageElement): string { return toDataUrl(img, 96, 0.7); }

/** 프롭 머티리얼 — 파이프라인의 PBR 맵을 표준 머티리얼에 꽂는다. 커스텀 셰이더 없음 → 컴파일 빠르고 glb 와 같다. */
function standardFromMaps(maps: MaterialMaps): { material: THREE.MeshStandardMaterial; dispose: () => void } {
  const tex = (r: Parameters<typeof rasterToCanvas>[0], srgb: boolean) => {
    const t = new THREE.CanvasTexture(rasterToCanvas(r));
    t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
    t.anisotropy = 8; t.needsUpdate = true; return t;
  };
  const map = tex(maps.albedo, true), normalMap = tex(maps.normal, false), orm = tex(maps.orm, false), emissiveMap = tex(maps.emissive, true);
  const material = new THREE.MeshStandardMaterial({
    map, normalMap, aoMap: orm, roughnessMap: orm, metalnessMap: orm, emissiveMap, emissive: new THREE.Color(0xffffff), emissiveIntensity: 0.6,
    roughness: 1, metalness: 1, normalScale: new THREE.Vector2(0.8, 0.8),
  });
  return { material, dispose: () => { for (const t of [map, normalMap, orm, emissiveMap]) t.dispose(); material.dispose(); } };
}

async function makeAsset(file: File, kind: TileKind, name: string): Promise<TileAsset> {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const image = toDataUrl(img, kind === 'prop' ? 768 : 512);
    if (kind === 'prop') {
      const out = await forgeFromBlob(file, 'auto', { resolution: 96 }, { backend: 'heuristic', maxSize: 512 });
      const geo = out.mesh.geometry;
      // uv2 = uv (aoMap 이 uv2 를 보던 시절의 관습; three 0.185 는 uv 를 그대로 쓰지만 안전하게)
      if (!geo.getAttribute('uv1') && geo.getAttribute('uv')) geo.setAttribute('uv1', geo.getAttribute('uv'));
      const { material, dispose: dm } = standardFromMaps(out.maps);
      out.material.dispose();
      const { object, dispose } = buildPropTemplate(geo, material, 0.92);
      return { id: newId(kind), name, kind, image, thumb: thumbOf(img), template: object, dispose: () => { dispose(); dm(); geo.dispose(); } };
    }
    const { object, dispose } = buildFlatTemplate(img, kind);
    return { id: newId(kind), name, kind, image, thumb: thumbOf(img), template: object, dispose };
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function assetFromDataUrl(a: MapDoc['assets'][number]): Promise<TileAsset> {
  const blob = await (await fetch(a.image)).blob();
  const file = new File([blob], `${a.name}.webp`, { type: blob.type || 'image/webp' });
  const asset = await makeAsset(file, a.kind, a.name);
  asset.id = a.id;
  return asset;
}

export const useMapStore = create<MapStore>((set, get) => ({
  doc: emptyDoc(),
  assetsVersion: 0,
  selected: null,
  tool: 'paint',
  rot: 0,
  level: 0,
  scale: 1,
  busy: null,
  error: null,
  showGrid: true,
  showHero: true,

  async addImages(files, kind) {
    set({ busy: `${files.length}장 → ${kind} 타일 만드는 중`, error: null });
    try {
      for (const f of files) {
        const name = f.name.replace(/\.[^.]+$/, '');
        const asset = await makeAsset(f, kind, name);
        mapAssets.set(asset.id, asset);
        set((s) => ({ doc: { ...s.doc, assets: [...s.doc.assets, { id: asset.id, name, kind, image: asset.image }] }, assetsVersion: s.assetsVersion + 1, selected: asset.id }));
      }
    } catch (err) {
      set({ error: err instanceof Error ? err.message : String(err) });
    } finally {
      set({ busy: null });
    }
  },

  async addFromUrl(url, name, kind) {
    const res = await fetch(url);
    const blob = await res.blob();
    await get().addImages([new File([blob], `${name}.webp`, { type: blob.type || 'image/webp' })], kind);
  },

  select: (selected) => set({ selected, tool: 'paint' }),
  setTool: (tool) => set({ tool }),
  setRot: (rot) => set({ rot: ((rot % 4) + 4) % 4 }),
  setLevel: (level) => set({ level: Math.max(0, Math.min(3, level)) }),
  setScale: (scale) => set({ scale: Math.max(0.3, Math.min(3, scale)) }),
  resize: (width, depth) => set((s) => ({ doc: { ...s.doc, width, depth, placements: s.doc.placements.filter((p) => p.x < width && p.z < depth) } })),

  paintAt(x, z) {
    const s = get();
    if (x < 0 || z < 0 || x >= s.doc.width || z >= s.doc.depth) return;
    if (s.tool === 'spawn') { set({ doc: { ...s.doc, spawn: { x, z, rot: s.rot } } }); return; }
    if (s.tool === 'erase') { set({ doc: eraseTile(s.doc, x, z, s.level) }); return; }
    if (!s.selected) return;
    const kind = s.doc.assets.find((a) => a.id === s.selected)?.kind;
    const p: Placement = { x, z, assetId: s.selected, rot: s.rot, level: kind === 'floor' ? 0 : s.level, scale: kind === 'prop' ? s.scale : 1 };
    set({ doc: placeTile(s.doc, p) });
  },

  fill() {
    const s = get();
    if (!s.selected) return;
    if (s.doc.assets.find((a) => a.id === s.selected)?.kind !== 'floor') return;
    set({ doc: fillFloor(s.doc, s.selected) });
  },

  clear: () => set((s) => ({ doc: { ...s.doc, placements: [], spawn: null } })),

  removeAsset(id) {
    mapAssets.get(id)?.dispose();
    mapAssets.delete(id);
    set((s) => ({
      doc: { ...s.doc, assets: s.doc.assets.filter((a) => a.id !== id), placements: s.doc.placements.filter((p) => p.assetId !== id) },
      assetsVersion: s.assetsVersion + 1,
      selected: s.selected === id ? null : s.selected,
    }));
  },

  async loadDoc(text) {
    set({ busy: '맵 문서 읽는 중', error: null });
    try {
      const doc = parseDoc(text);
      for (const a of mapAssets.values()) a.dispose();
      mapAssets.clear();
      for (const a of doc.assets) {
        set({ busy: `자산 복원: ${a.name}` });
        const asset = await assetFromDataUrl(a);
        mapAssets.set(asset.id, asset);
      }
      set((s) => ({ doc, assetsVersion: s.assetsVersion + 1, selected: doc.assets[0]?.id ?? null }));
    } catch (err) {
      set({ error: err instanceof Error ? err.message : String(err) });
    } finally {
      set({ busy: null });
    }
  },

  toggle: (k) => set((s) => ({ [k]: !s[k] }) as Partial<MapStore>),
}));
